import { AlertTitle, Box, Container, Divider, Fab, Link, Skeleton, Stack } from '@mui/material'
import NavigationHeader from '../../../components/NavigationHeader'
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Favorite, FmdGoodOutlined } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TextBoxOneLine from '../../../components/TextBoxOneLine'
import TextBoxTwoLines from '../../../components/TextBoxTwoLines'
import { useInnsynContext } from '../../../context/InnsynContextProvider'
import { SetStateAction, useEffect, useState } from 'react'
import { logIn } from '../../../services/LoginService'
import { FormattedMessage, useIntl } from 'react-intl'
import colors from '../../../styles/colors.scss'
import { INNSYN_SAVED_PATH, INNSYN_SEARCH_RESULTS_PATH } from '../../../paths'
import { getOmraadeNameKey } from '../../../services/TextService'

const TagDetailedInfo = () => {
    const { id } = useParams()
    const {
        geografi,
        merker,
        brukerInformasjon,
        fetchPersonerMedMerke,
        createAlert,
        konfigurasjon
    } = useInnsynContext()
    const intl = useIntl()
    const [aktoer, setAktoer] = useState<Person | undefined>()
    const [isFavorite, setIsFavorite] = useState<boolean>(false)
    const [query] = useSearchParams()
    const navigate = useNavigate()

    useEffect(() => {
        const currentStorage = localStorage.getItem('reinmerker-innsyn-favoritter-v1')
        if (id && currentStorage) {
            setIsFavorite(currentStorage.includes(id))
        }
    }, [id])

    const handleFavoriteStateChange = () => {
        if (isFavorite) {
            removeMerkeFromFavorites()
        } else {
            addMerkeToFavorites()
        }
    }

    const addMerkeToFavorites = () => {
        const currentStorage = localStorage.getItem('reinmerker-innsyn-favoritter-v1')
        const newStorage = `${currentStorage ? currentStorage : ''}${
            currentStorage && currentStorage?.length > 0 ? '&' : ''
        }${id}`
        localStorage.setItem('reinmerker-innsyn-favoritter-v1', newStorage)
        setIsFavorite(true)
    }
    const removeMerkeFromFavorites = () => {
        const currentStorage = localStorage.getItem(
            'reinmerker-innsyn-favoritter-v1'
        )
        if (!currentStorage) return
        if (id && currentStorage.includes(id)) {
            const allFavorites = currentStorage.split('&')
            let newStorage = ''
            // If in middle or last item
            if (allFavorites.length > 1 && !allFavorites[0].includes(id)) {
                newStorage = currentStorage.replace(`&${id}`, '')
                // If the very first item
            } else if (allFavorites.length > 1) {
                newStorage = currentStorage.replace(`${id}&`, '')
                // If the only item
            } else {
                newStorage = currentStorage.replace(`${id}`, '')
            }
            localStorage.setItem('reinmerker-innsyn-favoritter-v1', newStorage)
            setIsFavorite(false)
        }
    }

    const findOeremerke = (merker: Merke[], id: string) => {
        return merker.filter((merke: Merke) => merke.id === parseInt(id))[0]
    }

    const findSiida = (geografi: Area[], merke: Merke) => {
        return geografi
            .find((omraade) => merke?.omraadeId === omraade.omraadeId)
            ?.distrikter.find(
                (distrikt) => distrikt.distriktId === merke?.distriktId
            )
            ?.siidaer.find((siida) => siida.siidaId === merke?.siidaId)
    }

    const findDistrikt = (geografi: Area[], merke: Merke) => {
        return geografi
            .find((omraade) => merke?.omraadeId === omraade.omraadeId)
            ?.distrikter.find(
                (distrikt) => distrikt.distriktId === merke?.distriktId
            )
    }

    const findOmraade = (geografi: Area[], merke: Merke) => {
        return geografi.find(
            (omraade) => merke?.omraadeId === omraade.omraadeId
        )
    }

    useEffect(() => {
        if (brukerInformasjon?.harTilgang && id) {
            fetchPersonerMedMerke([id]).then(
                (res: SetStateAction<Person | undefined>[]) => setAktoer(res[0])
            )
        }
    }, [brukerInformasjon?.harTilgang])

    const getPersonOrAktoerName = () => {
        if (aktoer?.organisasjon?.orgnr !== null) {
            return (
                <div>
                    {aktoer?.organisasjon.orgnavn} {aktoer?.organisasjon.orgnr}
                </div>
            )
        } else {
            return `${aktoer?.personnavn.fornavn} ${aktoer?.personnavn.etternavn}`
        }
    }

    const CutOverlay = ({
                            fill = '#000000',
                            width = '100%',
                            height = '100%'
                        }) => (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            width={width}
            height={height}
            fill={fill}
            viewBox='0 0 422 150'>
            <title>Cut Overlay</title>
            <path
                fill='#FFFFFF'
                fillRule='evenodd'
                d='m211 148c0 1.1 0 2 0 2h-211v-150h422v150h-211c0 0-0.1-2 0-2c89.5 0 195.3-22.3 209-73c-10.6-48.5-123-73-195-73c0 0-14 15-14 15c0 0-14.5-15.1-14.5-15.1c-69.9 0-181.1 23.8-193.5 73.1c10.3 46.3 106.1 73 208 73z'
                clipRule='evenodd'></path>
        </svg>
    )

    return (
        <Container sx={{ position: 'relative', height: '100%' }}>
            <NavigationHeader
                rightText='button-start-page'
                onClickRightText={() => navigate('/app/search')}
                onClickBack={() => {
                    if (query.get('search_type')) {
                        navigate({
                            pathname: INNSYN_SEARCH_RESULTS_PATH,
                            search: `?${createSearchParams(query)}`
                        })
                    } else {
                        navigate({
                            pathname: INNSYN_SAVED_PATH
                        })
                    }
                    query.get('search_type') && query.delete('search_type')
                }}
            />
            <>
                {id && (
                    <>
                        <Stack
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                            {merker ? (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        minWidth: '60%',
                                        marginTop: '3rem'
                                    }}>
                                    <img
                                        style={{
                                            width: '100%'
                                        }}
                                        alt='Reinmerke-bilde'
                                        src={`data:image/gif;base64,${
                                            findOeremerke(merker, id)?.bilde
                                        }`}
                                    />

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            width: '100%',
                                            height: '102%'
                                        }}>
                                        <CutOverlay/>
                                    </Box>
                                </Box>
                            ) : (
                                <Skeleton
                                    animation='wave'
                                    width='350px'
                                    height='210px'
                                />
                            )}

                            <>
                                <TextBoxOneLine
                                    header={
                                        <FormattedMessage
                                            id={'tag-info.right-ear'}
                                        />
                                    }
                                    content={
                                        merker && id ? (
                                            findOeremerke(merker, id)
                                                ?.merkenavnHoeyre
                                        ) : (
                                            <Skeleton
                                                animation='wave'
                                                width='12rem'
                                            />
                                        )
                                    }
                                />
                                <TextBoxOneLine
                                    header={
                                        <FormattedMessage
                                            id={'tag-info.left-ear'}
                                        />
                                    }
                                    content={
                                        merker && id ? (
                                            findOeremerke(merker, id)
                                                ?.merkenavnVenstre
                                        ) : (
                                            <Skeleton
                                                animation='wave'
                                                width='12rem'
                                            />
                                        )
                                    }
                                />
                            </>
                        </Stack>
                        <Divider sx={{ marginTop: '1rem' }}/>
                        <Stack>
                            <Stack
                                direction='row'
                                sx={{ marginTop: '0.75rem' }}
                                justifyContent='space-between'>
                                <TextBoxTwoLines
                                    header={
                                        <FormattedMessage
                                            id={'tag-info.registered-on'}
                                        />
                                    }>
                                    {brukerInformasjon && brukerInformasjon.harTilgang ? (
                                        <Box>{getPersonOrAktoerName()}</Box>
                                    ) : (!brukerInformasjon?.foedselsnummer && konfigurasjon) ? (
                                        <Link
                                            underline='none'
                                            color='inherit'
                                            onClick={() => {
                                                if (navigator.onLine) {
                                                    logIn(konfigurasjon?.altinnUrl)
                                                } else {
                                                    createAlert({
                                                        body: (
                                                            <>
                                                                <AlertTitle>
                                                                    <FormattedMessage
                                                                        id='log-in.alert-no-internet-header'/>
                                                                </AlertTitle>
                                                                <FormattedMessage id='log-in.alert-no-internet'/>
                                                            </>
                                                        ),
                                                        severity: 'info',
                                                        uniqueId:
                                                            'log-in-offline'
                                                    })
                                                }
                                            }}
                                            sx={{
                                                width: 'fit-content',
                                                marginBottom: '0.5rem',
                                                borderBottom:
                                                    '3px solid #00a9d9',
                                                marginRight: '1rem'
                                            }}>
                                            <Box>
                                                <FormattedMessage
                                                    id={'tag-info.log-in'}
                                                />
                                            </Box>
                                        </Link>
                                    ) : ''}
                                </TextBoxTwoLines>
                                <Fab
                                    // @ts-ignore
                                    color={isFavorite ? 'primary' : 'neutral'}
                                    sx={{ marginTop: '0.25rem' }}
                                    aria-label='Legg til i favoritter'
                                    onClick={() => handleFavoriteStateChange()}>
                                    <Favorite
                                        sx={{
                                            color: 'common.white'
                                        }}
                                    />
                                </Fab>
                            </Stack>
                            <TextBoxTwoLines
                                header={
                                    <FormattedMessage
                                        id={'tag-info.tag-number'}
                                    />
                                }>
                                {merker && id ? (
                                    <Box>
                                        {findOeremerke(merker, id)?.merkenr}
                                    </Box>
                                ) : (
                                    <Skeleton animation='wave' width='4rem'/>
                                )}
                            </TextBoxTwoLines>
                            <TextBoxTwoLines
                                header={
                                    <FormattedMessage
                                        id={'tag-info.registered-in'}
                                    />
                                }>
                                {merker ? (
                                    <Stack
                                        direction='row'
                                        sx={{
                                            alignItems: 'center'
                                        }}>
                                        <Box>
                                            <FmdGoodOutlined
                                                sx={{
                                                    color: colors.iconGray,
                                                    marginRight: '0.5rem'
                                                }}
                                            />
                                        </Box>
                                        <Stack>
                                            <Box>
                                                {geografi &&
                                                    findOeremerke(merker, id) &&
                                                    intl.formatMessage({
                                                        id: getOmraadeNameKey(
                                                            findOmraade(
                                                                geografi,
                                                                findOeremerke(
                                                                    merker,
                                                                    id
                                                                )
                                                            )?.omraadeId
                                                        )
                                                    })}
                                                {', '}
                                                {geografi &&
                                                    findOeremerke(merker, id) &&
                                                    findDistrikt(
                                                        geografi,
                                                        findOeremerke(
                                                            merker,
                                                            id
                                                        )
                                                    )?.distriktNavn}
                                            </Box>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Skeleton
                                        animation='wave'
                                        width='12rem'
                                        height='2rem'
                                    />
                                )}
                            </TextBoxTwoLines>
                            {merker &&
                                findOeremerke(merker, id)?.distriktId ===
                                20 && (
                                    <TextBoxTwoLines
                                        header={
                                            <FormattedMessage
                                                id={'tag-info.siida'}
                                            />
                                        }>
                                        <Box>
                                            {geografi &&
                                                findSiida(
                                                    geografi,
                                                    findOeremerke(merker, id)
                                                )?.siidaNavn}
                                        </Box>
                                    </TextBoxTwoLines>
                                )}
                            {brukerInformasjon?.harTilgang && (
                                <TextBoxTwoLines
                                    header={
                                        <FormattedMessage
                                            id={'tag-info.siidaandel'}
                                        />
                                    }>
                                    <Box>{aktoer?.siidaandelNavn}</Box>
                                </TextBoxTwoLines>
                            )}
                            <TextBoxTwoLines
                                header={
                                    <FormattedMessage
                                        id={'tag-info.register-year'}
                                    />
                                }>
                                {merker ? (
                                    <Box>
                                        {merker &&
                                            findOeremerke(merker, id)
                                                ?.registreringsAar}
                                    </Box>
                                ) : (
                                    <Skeleton animation='wave' width='5rem'/>
                                )}
                            </TextBoxTwoLines>
                            {merker && findOeremerke(merker, id)?.utgaaende && (
                                <Stack
                                    direction='row'
                                    sx={{ marginTop: '1rem' }}>
                                    <InfoOutlinedIcon/>
                                    <Box
                                        sx={{
                                            marginLeft: '1rem',
                                            fontStyle: 'italic',
                                            marginTop: '0.25rem'
                                        }}>
                                        <FormattedMessage
                                            id={'tag-info.outgoing'}
                                        />
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    </>
                )}
            </>
        </Container>
    )
}

export default TagDetailedInfo
